/*
  Adds checked upsell products to the cart before replaying the main product form submit.
  Used by snippets/product-upsells.liquid.
*/

const FOCUSABLE_SELECTOR = [
  'a[href]',
  'button:not([disabled])',
  'select:not([disabled])',
  'textarea:not([disabled])',
  'input:not([disabled])',
  '[tabindex]:not([tabindex="-1"])',
].join(',');

class ProductUpsells extends HTMLElement {
  constructor() {
    super();
    this.handleChange = this.handleChange.bind(this);
    this.handleModalClick = this.handleModalClick.bind(this);
    this.handleModalSelectChange = this.handleModalSelectChange.bind(this);
    this.handleModalKeydown = this.handleModalKeydown.bind(this);
    this.handleSubmit = this.handleSubmit.bind(this);

    this.replayingSubmit = false;
    this.activeVariantCard = null;
    this.previousFocus = null;
    this.modalWasPortaled = false;
  }

  connectedCallback() {
    if (this.dataset.ready === 'true') return;
    this.dataset.ready = 'true';

    this.errorElement = this.querySelector('[data-upsell-error]');
    this.modal = this.querySelector('[data-upsell-modal]');
    this.modalProduct = this.querySelector('[data-upsell-modal-product]');
    this.modalSelect = this.querySelector('[data-upsell-modal-select]');
    this.modalImage = this.querySelector('[data-upsell-modal-image]');
    this.modalConfirm = this.modal ? this.modal.querySelector('[data-upsell-modal-confirm]') : null;
    this.form = this.findProductForm();

    if (this.modal && this.modal.parentElement !== document.body) {
      document.body.appendChild(this.modal);
      this.modalWasPortaled = true;
    }

    this.addEventListener('change', this.handleChange);

    if (this.modal) {
      this.modal.addEventListener('click', this.handleModalClick);
      document.addEventListener('keydown', this.handleModalKeydown);
    }

    if (this.modalSelect) {
      this.modalSelect.addEventListener('change', this.handleModalSelectChange);
    }

    if (this.form) {
      this.form.addEventListener('submit', this.handleSubmit, true);
    }
  }

  disconnectedCallback() {
    this.removeEventListener('change', this.handleChange);
    document.removeEventListener('keydown', this.handleModalKeydown);

    if (this.modal) {
      this.modal.removeEventListener('click', this.handleModalClick);
      if (this.modalWasPortaled && this.modal.parentElement === document.body) {
        this.modal.remove();
      }
    }

    if (this.modalSelect) {
      this.modalSelect.removeEventListener('change', this.handleModalSelectChange);
    }

    if (this.form) {
      this.form.removeEventListener('submit', this.handleSubmit, true);
    }

    document.documentElement.classList.remove('product-upsells-modal-open');
    this.removeAttribute('data-ready');
  }

  findProductForm() {
    const formId = this.getAttribute('data-product-form-id');

    if (formId) {
      const formById = document.getElementById(formId);
      if (formById) return formById;
    }

    const nearbyForm = this.closest('form[action*="/cart/add"]');
    if (nearbyForm) return nearbyForm;

    const section = this.closest('[id^="shopify-section-"], .shopify-section, section');
    if (section) {
      const formInSection = section.querySelector('form[action*="/cart/add"]');
      if (formInSection) return formInSection;
    }

    return document.querySelector('form[action*="/cart/add"]');
  }

  handleChange(event) {
    if (event.target.matches('[data-upsell-checkbox]')) {
      this.handleCheckboxChange(event.target);
    }

    if (event.target.matches('[data-upsell-variant-select]')) {
      this.updateVariant(event.target);
    }
  }

  handleCheckboxChange(checkbox) {
    if (!checkbox.checked) return;

    const card = checkbox.closest('[data-upsell-card]');
    const select = card?.querySelector('[data-upsell-variant-select]');

    if (select) {
      this.openVariantModal(card);
    }
  }

  updateVariant(select) {
    const card = select.closest('.product-upsells__card');
    if (!card) return;

    const checkbox = card.querySelector('[data-upsell-checkbox]');
    const selectedOption = select.options[select.selectedIndex];
    const price = card.querySelector('[data-upsell-price]');
    const image = card.querySelector('.product-upsells__image');

    if (checkbox && selectedOption) {
      checkbox.dataset.variantId = selectedOption.value;
      checkbox.disabled = selectedOption.disabled;
    }

    if (price && selectedOption?.dataset.price) {
      price.textContent = selectedOption.dataset.price;
    }

    if (image && selectedOption?.dataset.image) {
      image.src = selectedOption.dataset.image;

      if (selectedOption.dataset.imageSrcset) {
        image.srcset = selectedOption.dataset.imageSrcset;
      } else {
        image.removeAttribute('srcset');
      }

      if (selectedOption.dataset.imageSizes) {
        image.sizes = selectedOption.dataset.imageSizes;
      } else {
        image.removeAttribute('sizes');
      }

      if (selectedOption.dataset.imageAlt) {
        image.alt = selectedOption.dataset.imageAlt;
      }
    }
  }

  openVariantModal(card) {
    const sourceSelect = card.querySelector('[data-upsell-variant-select]');
    if (!this.modal || !this.modalSelect || !sourceSelect) return;

    this.activeVariantCard = card;
    this.previousFocus = document.activeElement;
    this.modalSelect.innerHTML = '';

    Array.from(sourceSelect.options).forEach((option) => {
      this.modalSelect.add(option.cloneNode(true));
    });

    this.modalSelect.value = sourceSelect.value;

    if (this.modalProduct) {
      this.modalProduct.textContent = card.dataset.upsellTitle || '';
    }

    this.updateModalState();

    this.modal.hidden = false;
    document.documentElement.classList.add('product-upsells-modal-open');

    window.requestAnimationFrame(() => {
      this.modalSelect.focus();
    });
  }

  updateModalState() {
    if (!this.modalSelect) return;
    const option = this.modalSelect.options[this.modalSelect.selectedIndex];

    if (this.modalImage) {
      const src = option?.dataset.image;
      if (src) {
        this.modalImage.src = src;
        this.modalImage.alt = option.dataset.imageAlt || '';
        this.modalImage.hidden = false;
      } else {
        this.modalImage.removeAttribute('src');
        this.modalImage.hidden = true;
      }
    }

    if (this.modalConfirm) {
      this.modalConfirm.disabled = !!option?.disabled;
    }
  }

  handleModalSelectChange() {
    this.updateModalState();
  }

  handleModalClick(event) {
    if (event.target.closest('[data-upsell-modal-confirm]')) {
      this.confirmVariantModal();
      return;
    }

    if (event.target.closest('[data-upsell-modal-close], [data-upsell-modal-cancel]')) {
      this.closeVariantModal();
    }
  }

  confirmVariantModal() {
    const sourceSelect = this.activeVariantCard?.querySelector('[data-upsell-variant-select]');
    if (sourceSelect && this.modalSelect) {
      sourceSelect.value = this.modalSelect.value;
      this.updateVariant(sourceSelect);
    }

    this.closeVariantModal({ keepChecked: true });
  }

  closeVariantModal(options = {}) {
    const { keepChecked = false } = options;
    const checkbox = this.activeVariantCard?.querySelector('[data-upsell-checkbox]');

    if (!keepChecked && checkbox) {
      checkbox.checked = false;
    }

    if (this.modal) {
      this.modal.hidden = true;
    }

    document.documentElement.classList.remove('product-upsells-modal-open');

    if (this.previousFocus && typeof this.previousFocus.focus === 'function') {
      this.previousFocus.focus();
    }

    this.activeVariantCard = null;
    this.previousFocus = null;
  }

  handleModalKeydown(event) {
    if (!this.modal || this.modal.hidden) return;

    if (event.key === 'Escape') {
      this.closeVariantModal();
      return;
    }

    if (event.key === 'Tab') {
      this.trapFocus(event);
    }
  }

  trapFocus(event) {
    const focusables = this.getModalFocusables();
    if (!focusables.length) return;

    const first = focusables[0];
    const last = focusables[focusables.length - 1];
    const active = document.activeElement;
    const insideModal = this.modal.contains(active);

    if (event.shiftKey) {
      if (!insideModal || active === first) {
        event.preventDefault();
        last.focus();
      }
    } else if (!insideModal || active === last) {
      event.preventDefault();
      first.focus();
    }
  }

  getModalFocusables() {
    if (!this.modal) return [];
    return Array.from(this.modal.querySelectorAll(FOCUSABLE_SELECTOR)).filter(
      (el) => !el.hasAttribute('hidden') && el.offsetParent !== null,
    );
  }

  selectedItems() {
    return Array.from(this.querySelectorAll('[data-upsell-checkbox]:checked:not(:disabled)'))
      .map((checkbox) => ({
        id: Number(checkbox.dataset.variantId),
        quantity: 1,
      }))
      .filter((item) => item.id);
  }

  async handleSubmit(event) {
    if (this.replayingSubmit) {
      this.replayingSubmit = false;
      return;
    }

    const items = this.selectedItems();
    if (!items.length) return;

    event.preventDefault();
    event.stopImmediatePropagation();
    this.setError('');

    const submitter = event.submitter || this.form.querySelector('[type="submit"]');
    this.setBusy(submitter, true);

    try {
      await this.addUpsells(items);
      this.setBusy(submitter, false);
      this.replayingSubmit = true;
      this.submitOriginalForm(submitter);
    } catch (error) {
      this.setBusy(submitter, false);
      this.setError(error.message || 'Unable to add selected extras. Please try again.');
    }
  }

  async addUpsells(items) {
    const root = window.Shopify?.routes?.root || '/';
    const response = await fetch(`${root}cart/add.js`, {
      method: 'POST',
      headers: {
        Accept: 'application/json',
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ items }),
    });

    if (response.ok) return response.json();

    let message = 'Unable to add selected extras. Please try again.';

    try {
      const error = await response.json();
      message = error.description || error.message || message;
    } catch (_error) {
      // Keep the generic message if Shopify returns a non-JSON error.
    }

    throw new Error(message);
  }

  submitOriginalForm(submitter) {
    if (submitter && typeof this.form.requestSubmit === 'function') {
      this.form.requestSubmit(submitter);
      return;
    }

    if (typeof this.form.requestSubmit === 'function') {
      this.form.requestSubmit();
      return;
    }

    this.form.submit();
  }

  setBusy(submitter, isBusy) {
    this.setAttribute('aria-busy', isBusy ? 'true' : 'false');

    if (submitter) {
      submitter.disabled = isBusy;
    }
  }

  setError(message) {
    if (!this.errorElement) return;

    this.errorElement.textContent = message;
    this.errorElement.hidden = !message;
  }
}

if (!customElements.get('product-upsells')) {
  customElements.define('product-upsells', ProductUpsells);
}